import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type AgencyContextFact = {
    readonly id: string;
    readonly title: string;
    readonly text: string;
    readonly placeholder?: string;
    // Starter rows only: the topic's own heading, attached automatically
    // when the admin types a plain answer with no heading of their own —
    // the row is otherwise genuinely empty, so its placeholder can show.
    readonly defaultTitle?: string;
};

export type AgencyContextStarterRow = {
    readonly key: string;
    readonly label: string;
    readonly prompt: string;
};

export type AgencyContextEditorValue = {
    readonly text: string;
    readonly dismissedSuggestedTopicKeys: ReadonlyArray<string>;
};

export type RemovedFact = {
    readonly fact: AgencyContextFact;
    readonly index: number;
    readonly dismissedStarterKey?: string;
};

// Shown on any row with no content: a fresh "Add" row, and a saved row the
// admin typed over until nothing was left.
export const DEFAULT_FACT_PLACEHOLDER = 'Add an agency fact.';

// One physical line keeps multiline facts lossless through pre-deploy parsers.
const DETAIL_SEPARATOR = '\u2028';
const MARKDOWN_HEADING = /^(\s*)(\\*#{1,6})(?=\s|$)/;

function escapeMarkdownHeading(line: string): string {
    return line.replace(MARKDOWN_HEADING, '$1\\$2');
}

export function parseAgencyContextFact(
    text: string,
): Omit<AgencyContextFact, 'id'> {
    const firstLineEnd = text.indexOf('\n');
    const firstLine = firstLineEnd === -1 ? text : text.slice(0, firstLineEnd);
    const separator = firstLine.indexOf(': ');
    if (separator <= 0) {
        return { title: '', text };
    }
    return {
        title: text.slice(0, separator),
        text: text.slice(separator + 2),
    };
}

/**
 * Split a saved document into top-level facts.
 *
 * Mirrors `parse_agency_context_facts` in
 * `libraries/python/agency_context/store.py`: a top-level bullet or legacy
 * prose line starts a fact, while indented lines remain nested detail.
 */
export function parseAgencyContextFacts(text: string): Array<string> {
    const facts: Array<string> = [];
    let currentLines: Array<string> = [];
    const finishCurrentFact = () => {
        while (
            currentLines.length > 0 &&
            currentLines[currentLines.length - 1] === ''
        ) {
            currentLines.pop();
        }
        const fact = currentLines.join('\n').trim();
        if (fact.length > 0) {
            facts.push(fact);
        }
        currentLines = [];
    };

    for (const rawLine of text.replace(/\r\n?/g, '\n').split('\n')) {
        const line = rawLine.trim();
        if (line.length === 0) {
            if (
                currentLines.length > 0 &&
                currentLines[currentLines.length - 1] !== ''
            ) {
                currentLines.push('');
            }
            continue;
        }
        if (/^\s/.test(rawLine) && currentLines.length > 0) {
            const continuation = rawLine.startsWith('  ')
                ? rawLine.slice(2)
                : rawLine.slice(1);
            currentLines.push(continuation.trimEnd());
            continue;
        }
        finishCurrentFact();
        const fact =
            line.startsWith('-') || line.startsWith('*') || line.startsWith('•')
                ? line.slice(1).trim()
                : line;
        currentLines = fact.split(DETAIL_SEPARATOR);
    }
    finishCurrentFact();
    return facts;
}

function composeAgencyContextFact(fact: AgencyContextFact): string {
    const text = fact.text.trim();
    if (!text) {
        return '';
    }
    const title = fact.title.trim();
    return title ? `${title}: ${text}` : text;
}

function normalizedHeading(value: string): string {
    return value.trim().toLocaleLowerCase();
}

export function composeAgencyContextDocument(
    facts: ReadonlyArray<AgencyContextFact>,
): string {
    return facts
        .map(composeAgencyContextFact)
        .filter((fact) => fact.length > 0)
        .map(
            (fact) =>
                `- ${fact
                    .replace(/\r\n?/g, '\n')
                    .split('\n')
                    .join(DETAIL_SEPARATOR)}`,
        )
        .join('\n');
}

export function composeAgencyContextMarkdownDocument(text: string): string {
    return parseAgencyContextFacts(text)
        .map((fact) =>
            fact
                .replace(/\r\n?/g, '\n')
                .split('\n')
                .map((line, index) =>
                    index === 0
                        ? `- ${line}`
                        : `  ${escapeMarkdownHeading(line)}`,
                )
                .join('\n\n'),
        )
        .join('\n');
}

function seed(
    text: string,
    starterRows: ReadonlyArray<AgencyContextStarterRow>,
    dismissedSuggestedTopicKeys: ReadonlyArray<string>,
): Array<AgencyContextFact> {
    const savedFacts = parseAgencyContextFacts(text).map((factText, index) => ({
        id: `saved-${index}`,
        ...parseAgencyContextFact(factText),
    }));
    const dismissed = new Set(dismissedSuggestedTopicKeys);
    const savedTitles = new Set(
        savedFacts.map((fact) =>
            normalizedHeading(fact.title.length > 0 ? fact.title : fact.text),
        ),
    );
    const starterFacts = starterRows
        .filter(
            (starterRow) =>
                !dismissed.has(starterRow.key) &&
                !savedTitles.has(normalizedHeading(starterRow.label)),
        )
        .map((starterRow) => ({
            id: `starter-${starterRow.key}`,
            title: '',
            text: '',
            placeholder: `${starterRow.label}: ${starterRow.prompt}`,
            defaultTitle: starterRow.label,
        }));
    return [...savedFacts, ...starterFacts];
}

/**
 * Draft state for the Agency guide document.
 *
 * A completed row stays one agent-readable fact in the existing persisted
 * document. Starter rows only provide a writing prompt, so an untouched
 * guide remains empty.
 */
export function useAgencyContextEditor(
    savedText: string,
    starterRows: ReadonlyArray<AgencyContextStarterRow>,
    savedDismissedSuggestedTopicKeys: ReadonlyArray<string> = [],
) {
    const nextId = useRef(0);
    const [facts, setFacts] = useState<ReadonlyArray<AgencyContextFact>>(() =>
        seed(savedText, starterRows, savedDismissedSuggestedTopicKeys),
    );
    const current = useRef(facts);
    const [dismissedSuggestedTopicKeys, setDismissedSuggestedTopicKeys] =
        useState<ReadonlyArray<string>>(() =>
            [...savedDismissedSuggestedTopicKeys].sort(),
        );
    const currentDismissedSuggestedTopicKeys = useRef(
        dismissedSuggestedTopicKeys,
    );
    const base = useRef<AgencyContextEditorValue>({
        text: savedText,
        dismissedSuggestedTopicKeys: [
            ...savedDismissedSuggestedTopicKeys,
        ].sort(),
    });
    const commit = useCallback((next: ReadonlyArray<AgencyContextFact>) => {
        current.current = next;
        setFacts(next);
        return next;
    }, []);
    const commitDismissedSuggestedTopicKeys = useCallback(
        (next: ReadonlyArray<string>) => {
            const normalized = [...new Set(next)].sort();
            currentDismissedSuggestedTopicKeys.current = normalized;
            setDismissedSuggestedTopicKeys(normalized);
            return normalized;
        },
        [],
    );
    const valueFor = useCallback(
        (
            nextFacts: ReadonlyArray<AgencyContextFact>,
        ): AgencyContextEditorValue => ({
            text: composeAgencyContextDocument(nextFacts),
            dismissedSuggestedTopicKeys:
                currentDismissedSuggestedTopicKeys.current,
        }),
        [],
    );

    const document = useMemo(
        () => composeAgencyContextDocument(facts),
        [facts],
    );
    const value = useMemo<AgencyContextEditorValue>(
        () => ({ text: document, dismissedSuggestedTopicKeys }),
        [dismissedSuggestedTopicKeys, document],
    );

    const addFact = useCallback((): AgencyContextEditorValue => {
        nextId.current += 1;
        const nextFacts = commit([
            ...current.current,
            {
                id: `added-${nextId.current}`,
                title: '',
                text: '',
                placeholder: DEFAULT_FACT_PLACEHOLDER,
            },
        ]);
        return valueFor(nextFacts);
    }, [commit, valueFor]);

    const updateFact = useCallback(
        (
            id: string,
            update: Pick<AgencyContextFact, 'title' | 'text'>,
        ): AgencyContextEditorValue => {
            if (id.startsWith('starter-')) {
                const starterKey = id.slice('starter-'.length);
                // Compare against the starter's own label, not the previous
                // keystroke's value: comparing to the previous value
                // dismissed on the first keystroke with no way back, even
                // when the user retyped the original heading.
                const starterRow = starterRows.find(
                    (row) => row.key === starterKey,
                );
                const matchesOriginalLabel =
                    starterRow !== undefined &&
                    normalizedHeading(update.title) ===
                        normalizedHeading(starterRow.label);
                const isDismissed =
                    currentDismissedSuggestedTopicKeys.current.includes(
                        starterKey,
                    );
                if (!matchesOriginalLabel && !isDismissed) {
                    commitDismissedSuggestedTopicKeys([
                        ...currentDismissedSuggestedTopicKeys.current,
                        starterKey,
                    ]);
                } else if (matchesOriginalLabel && isDismissed) {
                    commitDismissedSuggestedTopicKeys(
                        currentDismissedSuggestedTopicKeys.current.filter(
                            (key) => key !== starterKey,
                        ),
                    );
                }
            }
            const nextFacts = commit(
                current.current.map((fact) =>
                    fact.id === id ? { ...fact, ...update } : fact,
                ),
            );
            return valueFor(nextFacts);
        },
        [commit, commitDismissedSuggestedTopicKeys, starterRows, valueFor],
    );

    const removeFact = useCallback(
        (id: string): RemovedFact | undefined => {
            const index = current.current.findIndex((fact) => fact.id === id);
            if (index === -1) {
                return undefined;
            }
            const removed = current.current[index]!;
            commit(current.current.filter((fact) => fact.id !== id));
            const starterKey = id.startsWith('starter-')
                ? id.slice('starter-'.length)
                : starterRows.find(
                      (starterRow) =>
                          normalizedHeading(starterRow.label) ===
                          normalizedHeading(removed.title),
                  )?.key;
            if (starterKey !== undefined) {
                commitDismissedSuggestedTopicKeys([
                    ...currentDismissedSuggestedTopicKeys.current,
                    starterKey,
                ]);
            }
            return { fact: removed, index, dismissedStarterKey: starterKey };
        },
        [commit, commitDismissedSuggestedTopicKeys, starterRows],
    );

    // Snapshot handed back by `removeFact` so a caller can offer Undo:
    // where the fact sat, and which suggested-topic dismissal (if any) to
    // reverse alongside it.
    const restoreFact = useCallback(
        (removed: RemovedFact): void => {
            commit([
                ...current.current.slice(0, removed.index),
                removed.fact,
                ...current.current.slice(removed.index),
            ]);
            if (removed.dismissedStarterKey !== undefined) {
                commitDismissedSuggestedTopicKeys(
                    currentDismissedSuggestedTopicKeys.current.filter(
                        (key) => key !== removed.dismissedStarterKey,
                    ),
                );
            }
        },
        [commit, commitDismissedSuggestedTopicKeys],
    );

    const resetToSaved = useCallback(
        (value: AgencyContextEditorValue) => {
            base.current = value;
            commitDismissedSuggestedTopicKeys(
                value.dismissedSuggestedTopicKeys,
            );
            commit(
                seed(
                    value.text,
                    starterRows,
                    value.dismissedSuggestedTopicKeys,
                ),
            );
        },
        [commit, commitDismissedSuggestedTopicKeys, starterRows],
    );

    const savedDismissedTopicKeys = useMemo(
        () => [...savedDismissedSuggestedTopicKeys].sort(),
        [savedDismissedSuggestedTopicKeys],
    );
    const savedDismissedTopicKey = savedDismissedTopicKeys.join('\n');
    useEffect(() => {
        const hasDirtyDraft =
            composeAgencyContextDocument(current.current) !==
                base.current.text ||
            currentDismissedSuggestedTopicKeys.current.join('\n') !==
                base.current.dismissedSuggestedTopicKeys.join('\n');
        if (
            base.current.text === savedText &&
            base.current.dismissedSuggestedTopicKeys.join('\n') ===
                savedDismissedTopicKey
        ) {
            return;
        }
        if (hasDirtyDraft) {
            return;
        }
        resetToSaved({
            text: savedText,
            dismissedSuggestedTopicKeys: savedDismissedTopicKeys,
        });
    }, [
        resetToSaved,
        savedDismissedTopicKey,
        savedDismissedTopicKeys,
        savedText,
    ]);

    const markSaved = useCallback(
        (value: AgencyContextEditorValue) => {
            base.current = value;
            const savedIdsByFact = new Map<string, Array<string>>();
            for (const [index, savedFact] of parseAgencyContextFacts(
                value.text,
            ).entries()) {
                const ids = savedIdsByFact.get(savedFact) ?? [];
                ids.push(`saved-${index}`);
                savedIdsByFact.set(savedFact, ids);
            }
            commit(
                current.current.map((fact) => {
                    const serialized = composeAgencyContextFact(fact);
                    const savedIds = savedIdsByFact.get(serialized);
                    const savedId = savedIds?.shift();
                    // Drop `placeholder`/`defaultTitle`: a starter row
                    // promoted to a saved id must behave like any other
                    // saved fact, not keep re-attaching its old heading
                    // if the admin later clears it.
                    return savedId === undefined
                        ? fact
                        : { id: savedId, title: fact.title, text: fact.text };
                }),
            );
        },
        [commit],
    );

    return {
        facts,
        document,
        value,
        characterCount: document.length,
        isDirty:
            document !== base.current.text ||
            dismissedSuggestedTopicKeys.join('\n') !==
                base.current.dismissedSuggestedTopicKeys.join('\n'),
        base,
        addFact,
        updateFact,
        removeFact,
        restoreFact,
        resetToSaved,
        markSaved,
    };
}
